import {PropertyDefinitionForm} from '@github-ui/custom-properties-core/components/PropertyDefinitionForm'
import type {usePropertyDefinitionForm} from '@github-ui/custom-properties-core/hooks/use-property-definition-form'
import type {useRefs} from '@github-ui/custom-properties-core/hooks/use-refs'
import {Banner} from '@primer/react'

import type {RepoPropertyDefinitionWithValueSchema} from '../hooks/use-definition-form-with-value'
import styles from './MultiStepForm.module.css'
import type {Repository} from '../types'

type RefProps = ReturnType<typeof useRefs<RepoPropertyDefinitionWithValueSchema>>
type FormProps = ReturnType<typeof usePropertyDefinitionForm<RepoPropertyDefinitionWithValueSchema>>

interface Props extends FormProps, RefProps {
  sourceName: string
  repositories: Repository[]
}

const MAX_VISIBLE_REPOS = 5

export function ApplyValueStep({repositories, sourceName, updateField, getFieldValue, getFieldError, setRef}: Props) {
  const reposCount = repositories.length
  const visibleRepos = repositories.slice(0, MAX_VISIBLE_REPOS)
  const hiddenReposCount = reposCount - visibleRepos.length

  return (
    <div className={styles.stepContent}>
      <PropertyDefinitionForm>
        <p id="apply-value-repo-list-label" className="text-bold mb-2">
          For the following repositories:
        </p>

        {repositories.length > 0 && (
          <ul aria-labelledby="apply-value-repo-list-label" className={styles.repoNamesList}>
            {visibleRepos.map((repo, index) => (
              <li key={repo.id}>
                {sourceName}/{repo.name}
                {hiddenReposCount > 0 && index === visibleRepos.length - 1 && (
                  <span>{` and ${hiddenReposCount} more`}</span>
                )}
              </li>
            ))}
          </ul>
        )}

        <PropertyDefinitionForm.PropertyValueField
          idPrefix="applied-value"
          setInputRef={setRef('appliedValue')}
          className="mb-3"
          value={getFieldValue('appliedValue') || ''}
          label="Apply the following value:"
          onChange={value => updateField('appliedValue', value)}
          valueType={getFieldValue('valueType')}
          propertyName={getFieldValue('propertyName')}
          allowedValues={getFieldValue('allowedValues')}
          error={getFieldError('appliedValue')}
        />
      </PropertyDefinitionForm>

      <Banner
        title="When you save:"
        variant="info"
        description={
          <ul className={styles.saveSummaryList}>
            <li>This property will be set to this value on the {reposCount} selected repositories.</li>
            <li>This ruleset will be updated to target repositories by this property and value instead of by name.</li>
          </ul>
        }
      />
    </div>
  )
}
